import React, {useState, useEffect, useRef} from 'react';
import {Autocomplete, TextField, FormControl} from '@mui/material';
import {useInfiniteQuery} from '@tanstack/react-query';
import {getOrderShiptos} from "../services/IsfaService";

const getPlaceholderLabel = (label) => (label ? label.replace(/ \*$/, "") : "");

const ShiptoSearch = ({
    label, businessUnitCode, billtoId, billtoRequired = false, editable, required, initialValue = '', onChange,
}) => {

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState(initialValue);
    const [options, setOptions] = useState([]);
    const debounceTimeout = useRef(null);

    useEffect(() => {
        if (initialValue !== '') {
            setInputValue(initialValue);
        }
    }, [initialValue]);

    const queryEnabled = !!businessUnitCode && (!billtoRequired || !!billtoId) && (open || inputValue !== '');

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['orderShiptos', businessUnitCode, billtoId, inputValue],
        queryFn: ({pageParam}) => getOrderShiptos(pageParam, 10, {
            businessUnitCode,
            billtoId: billtoId || undefined,
            searchString: inputValue,
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.data.length ? allPages.length : undefined;
        },
        enabled: queryEnabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
    });

    useEffect(() => {
        if (data) {
            const fetchedOptions = data.pages.flatMap(page => page.data);
            setOptions(fetchedOptions);

            // search option where shiptoId matches inputValue
            const found = fetchedOptions.find(option => option.shiptoId === inputValue);
            if (found) {
                setValue(found);
                onChange(found);
            }
        }
    }, [data]);

    const handleScroll = (event) => {
        const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
        if (bottom && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Explains why the field is disabled, in order of precedence.
    const helperText = !businessUnitCode
        ? "Business Unit must be selected first"
        : (billtoRequired && !billtoId ? "Bill-to must be selected first" : "");

    const handleInputChange = (event, newInputValue) => {
        // If the input is changed by user typing (event exists), clear the selection
        if (event && event.type === 'change') {
            setValue(null);
            onChange(null);
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            const substringBeforeSpace = newInputValue.split(' ')[0];
            setInputValue(substringBeforeSpace);
        }, 500);
    };

    return (
        <FormControl fullWidth size="small" margin="dense" required={required}>
            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={handleClose}
                onChange={(event, newValue) => {
                    setValue(newValue);
                    onChange(newValue);
                }}
                getOptionLabel={(option) => {
                    // shiptoId - name (billto: billtoId, city: city)
                    let label = option.shiptoId;
                    if (option.name) {
                        label += ' - ' + option.name;
                    }
                    label += ' (Bill-to: ' + option.billtoId + ', City: ' + option.city + ')';
                    return label;
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.shiptoId}>
                        {option.shiptoId}{option.name ? ' - ' + option.name : ''}
                        {' (Bill-to: ' + option.billtoId + ', City: ' + option.city + ')'}
                    </li>
                )}
                options={options}
                loading={isFetching}
                onInputChange={handleInputChange}
                disabled={!editable || !businessUnitCode || (billtoRequired && !billtoId)}
                ListboxProps={{onScroll: handleScroll, sx: {maxHeight: 200}}}
                isOptionEqualToValue={(option, value) => option.shiptoId === value.shiptoId}
                value={value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        size="small"
                        required={required}
                        inputProps={{...params.inputProps, placeholder: `Select ${getPlaceholderLabel(label)}`}}
                        sx={{'& input::placeholder': {color: 'rgba(0, 0, 0, 0.38)', opacity: 1}}}
                        helperText={helperText}
                    />
                )}
            />
        </FormControl>
    );
};

export default ShiptoSearch;
